import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Popover } from 'antd';
import dayjs from 'dayjs';
import {
  FiPlus,
  FiFilter,
  FiDownload,
  FiFolder,
  FiGrid,
  FiClock,
  FiCheckCircle,
  FiXCircle,
  FiCheckSquare,
  FiEdit2,
  FiEye,
  FiTrash2,
  FiMoreVertical,
} from 'react-icons/fi';

import firebase from '@config/firebase.config';
import { regularizationActionTypes } from '@store/Regularization/Regularization.actionTypes';
import { teamActionTypes } from '@store/Team/Team.actionTypes';
import { useTeamMemberDetailsForSelect as useTeamMemberPicker } from '@hooks/useTeamMemberDetailsForSelect';
import { toDateValue } from '@/utils/firestoreDate';
import { getHashedColor } from '@/utils/colorHash';
import { escapeCSVField, downloadCSV } from '@/utils/csvExport';

import { Text, textVariantsEnum, fontWeightEnum } from '@/components/Text';
import { StatCard } from '@/components/StatCard';
import { StatusBadge, statusBadgeTones } from '@/components/StatusBadge';
import { InitialsBadge } from '@/components/InitialsBadge';
import { Dropdown, borderedSelectStyles } from '@/components/Dropdown';
import { DateRangePicker } from '@/components/DateRangePicker';
import { Button, buttonVariants } from '@/components/Button';
import { Table } from '@/components/Table';
import { Pagination } from '@/components/Pagination';
import { EmptyState } from '@/components/EmptyState';
import { Spinner } from '@/components/Spinner/Spinner.component';
import { ActionsMenu } from '@components/ActionsMenu';
import { ConfirmActionDialog } from '@/components/ConfirmActionDialog';
import { addNotification, notificationStates } from '@components/Notification';

import { RegularizationModal } from './RegularizationModal.component';
import { UpdateRegularizationModal } from './UpdateRegularizationModal.component';
import { ViewRegularizationModal } from './ViewRegularizationModal.component';
import { REG_STATUS_TONE } from './Regularization.helper';
import styles from './Regularization.module.scss';
import { IS_DEMO_MODE } from '@/utils/demoMode';
import {
  DEMO_REGULARIZATION_LIST,
  DEMO_TEAM_PROJECTS,
  DEMO_MEMBER_DETAILS,
} from './Regularization.demoData';

const database = firebase.firestore();
const PAGE_SIZE = 10;

const REG_STATUS_FILTER_OPTIONS = [
  { value: null, label: 'All Status' },
  { value: 'Pending', label: 'Pending' },
  { value: 'Approved', label: 'Approved' },
  { value: 'Declined', label: 'Declined' },
];
const STAT_CARDS_CONFIG = [
  {
    key: 'total',
    label: 'Total Requests',
    caption: 'All time',
    icon: <FiGrid />,
    iconBg: 'var(--purple-100)',
  },
  {
    key: 'pending',
    label: 'Pending',
    caption: 'Awaiting action',
    icon: <FiClock />,
    iconBg: 'var(--warning-100)',
  },
  {
    key: 'approved',
    label: 'Approved',
    caption: 'This month',
    icon: <FiCheckCircle />,
    iconBg: 'var(--success-100)',
  },
  {
    key: 'declined',
    label: 'Declined',
    caption: 'This month',
    icon: <FiXCircle />,
    iconBg: 'var(--error-100)',
  },
  {
    key: 'completed',
    label: 'Completed',
    caption: 'This month',
    icon: <FiCheckSquare />,
    iconBg: 'var(--blue-100)',
  },
];

function PlainCell({ children }) {
  return (
    <Text variant={textVariantsEnum.Text_sm} fontWeight={fontWeightEnum.Normal}>
      {children}
    </Text>
  );
}

function DateCell({ date }) {
  if (!date) return <PlainCell>—</PlainCell>;
  return (
    <div className={styles.dateCell}>
      <Text
        variant={textVariantsEnum.Text_sm}
        fontWeight={fontWeightEnum.Medium}
      >
        {date.toLocaleDateString(undefined, {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        })}
      </Text>
      <Text
        variant={textVariantsEnum.Text_xs}
        fontWeight={fontWeightEnum.Normal}
        color="gray-500"
      >
        ({date.toLocaleDateString(undefined, { weekday: 'short' })})
      </Text>
    </div>
  );
}

function ReportDateCell({ date }) {
  if (!date) return <PlainCell>—</PlainCell>;
  return (
    <div className={styles.dateCell}>
      <Text
        variant={textVariantsEnum.Text_sm}
        fontWeight={fontWeightEnum.Medium}
      >
        {date.toLocaleDateString(undefined, {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        })}
      </Text>
      <Text
        variant={textVariantsEnum.Text_xs}
        fontWeight={fontWeightEnum.Normal}
        color="gray-500"
      >
        {date.toLocaleTimeString(undefined, {
          hour: '2-digit',
          minute: '2-digit',
        })}
      </Text>
    </div>
  );
}

function ProjectCell({ projectName }) {
  const color = getHashedColor(projectName || 'project');
  return (
    <div className={styles.departmentCell}>
      <span
        className={styles.departmentIconWrap}
        style={{ backgroundColor: `var(--${color}-100)` }}
      >
        <FiFolder size={14} color={`var(--${color}-700)`} />
      </span>
      <Text
        variant={textVariantsEnum.Text_sm}
        fontWeight={fontWeightEnum.Medium}
      >
        {projectName || 'N/A'}
      </Text>
    </div>
  );
}

function AssigneeCell({ member }) {
  if (!member) return <PlainCell>—</PlainCell>;
  const displayName = member.userName || member.userEmailid;
  return (
    <div className={styles.personCell}>
      <InitialsBadge label={displayName} twoLetter />
      <div className={styles.personCellText}>
        <Text
          variant={textVariantsEnum.Text_sm}
          fontWeight={fontWeightEnum.Medium}
        >
          {displayName}
        </Text>
        <Text
          variant={textVariantsEnum.Text_xs}
          fontWeight={fontWeightEnum.Normal}
          color="gray-500"
        >
          {member.userType || '—'}
        </Text>
      </div>
    </div>
  );
}

export const RegularizationComponent = () => {
  const { activeCompany } = useSelector((state) => state.auth.profile);
  const { profile } = useSelector((state) => state.auth);
  const teams = useSelector((state) => ({
    projects: state.team.projects,
    active: state.team.active,
    teams: state?.team?.teams,
  }));
  const regularizationState = useSelector(
    (state) => state.regularization || {}
  );
  const dispatch = useDispatch();

  const activeTeamId = teams.active?.activeTeamid ?? null;
  const { memberDetails } = useTeamMemberPicker(activeTeamId);

  const teamProjects = useMemo(
    () =>
      Array.isArray(teams.projects)
        ? teams.projects.filter((item) => !item.data?.deleted)
        : [],
    [teams.projects]
  );

  const [loading, setLoading] = useState(false);
  const [regList, setRegList] = useState([]);

  // Demo mode: no real backend data exists for the demo profile, so
  // GET_REQUESTS/GET_PROJECT and the member-details hook all resolve empty —
  // override at the point of use so the page shows realistic regularization
  // requests instead of an empty state. Real dispatches/state above keep
  // running unchanged.
  const displayRegList = IS_DEMO_MODE ? DEMO_REGULARIZATION_LIST : regList;
  const displayTeamProjects = IS_DEMO_MODE ? DEMO_TEAM_PROJECTS : teamProjects;
  const displayMemberDetails = IS_DEMO_MODE
    ? DEMO_MEMBER_DETAILS
    : memberDetails;
  const displayLoading = IS_DEMO_MODE ? false : loading;

  const [showAddRequestModal, setShowAddRequestModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const selectedRowData = useRef(null);
  const [activeMenuRow, setActiveMenuRow] = useState(null);
  const [viewRequest, setViewRequest] = useState(null);
  const [deleteRequest, setDeleteRequest] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const menuRef = useRef(null);

  const [dateRange, setDateRange] = useState(() => [
    dayjs().startOf('month').toDate(),
    dayjs().endOf('month').toDate(),
  ]);
  const [statusFilter, setStatusFilter] = useState(
    REG_STATUS_FILTER_OPTIONS[0]
  );
  const [departmentFilter, setDepartmentFilter] = useState(null);
  const [sortedBy, setSortedBy] = useState(null);
  const [reverseSort, setReverseSort] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const fetchRegulationRequests = () => {
    if (!activeCompany?.companyId || !profile?.id) return;
    setLoading(true);
    dispatch({
      type: regularizationActionTypes.GET_REQUESTS,
      payload: {
        userId: profile.id,
        companyId: activeCompany.companyId,
      },
    });
  };

  const fetchRegulationRequestsRef = React.useRef(fetchRegulationRequests);
  fetchRegulationRequestsRef.current = fetchRegulationRequests;

  useEffect(() => {
    if (regularizationState.requests) {
      setRegList(regularizationState.requests);
      setLoading(regularizationState.loading || false);
    }
  }, [regularizationState.requests, regularizationState.loading]);

  const fetchProjectTasks = useCallback(
    async (projectId, callback) => {
      // Demo mode: demo project ids don't exist in the real taskDatabase
      // collection, so this Firestore lookup would just resolve empty —
      // skip it entirely (same guard used for Projects.component.jsx's
      // getTasksNames).
      if (IS_DEMO_MODE) return;
      if (!projectId || !profile?.id) {
        return;
      }
      try {
        const snapshot = await database
          .collection('taskDatabase')
          .where('ProjectID', '==', projectId)
          .get();

        const tasks = snapshot.docs
          .map((doc) => ({ id: doc.id, ...doc.data() }))
          .filter((task) => task.Assignee === profile.id && !task.Deleted);
        if (callback) {
          callback(tasks);
        }
      } catch (error) {
        console.error('Error fetching project tasks:', error);
      }
    },
    [profile?.id]
  );

  const openUpdateModal = useCallback(
    (rowData) => {
      selectedRowData.current = rowData;
      setSelectedRequest(rowData);
      if (rowData.projectId) {
        fetchProjectTasks(rowData.projectId);
      }
      setShowUpdateModal(true);
    },
    [fetchProjectTasks]
  );

  const onActionsButtonClick = (e, rowData) => {
    setActiveMenuRow(rowData);
    menuRef.current.toggle(e);
  };

  const handleDeleteRequest = async () => {
    if (!deleteRequest || isDeleting) return;
    if (IS_DEMO_MODE) {
      addNotification({
        message:
          'Deleting regularization requests is not available in demo mode.',
        level: notificationStates.info,
      });
      setDeleteRequest(null);
      return;
    }
    setIsDeleting(true);
    const result = await new Promise((resolve) => {
      dispatch({
        type: regularizationActionTypes.DELETE_REQUEST,
        payload: {
          requestId: deleteRequest.id,
          companyId: activeCompany.companyId,
          assigneeId: deleteRequest.assignee,
          userId: profile.id,
          onComplete: resolve,
        },
      });
    });
    setIsDeleting(false);
    if (result?.success) {
      setDeleteRequest(null);
    }
  };

  const actionsMenuItems = useMemo(() => {
    if (!activeMenuRow) return [];
    const items = [];
    if (activeMenuRow.status !== 'Declined') {
      items.push({
        label: 'Edit',
        icon: <FiEdit2 size={16} />,
        onClick: () => {
          openUpdateModal(activeMenuRow);
          menuRef.current?.hide();
        },
      });
    }
    items.push({
      label: 'View',
      icon: <FiEye size={16} />,
      onClick: () => {
        setViewRequest(activeMenuRow);
        menuRef.current?.hide();
      },
    });
    if (activeMenuRow.status !== 'Declined') {
      items.push({
        label: 'Delete',
        icon: <FiTrash2 size={16} />,
        danger: true,
        onClick: () => {
          setDeleteRequest(activeMenuRow);
          menuRef.current?.hide();
        },
      });
    }
    return items;
  }, [activeMenuRow, openUpdateModal]);

  useEffect(() => {
    if (activeTeamId && activeCompany?.companyId) {
      dispatch({
        type: teamActionTypes.GET_PROJECT,
        payload: { companyId: activeCompany.companyId },
      });
      fetchRegulationRequestsRef.current();
    }
    // Intentionally omit fetchRegulationRequests so this only runs when team/company id changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTeamId, activeCompany?.companyId, dispatch]);

  const departmentFilterOptions = useMemo(() => {
    const names = [
      ...new Set(displayRegList.map((r) => r.teamName).filter(Boolean)),
    ];
    return [
      { value: null, label: 'All Departments' },
      ...names.map((name) => ({ value: name, label: name })),
    ];
  }, [displayRegList]);

  const dateFilteredRows = useMemo(() => {
    const [start, end] = dateRange || [];
    if (!start || !end) return displayRegList;
    return displayRegList.filter((row) => {
      const d = toDateValue(row.startTime);
      return Boolean(d) && d >= start && d <= end;
    });
  }, [displayRegList, dateRange]);

  const filteredRows = useMemo(() => {
    return dateFilteredRows.filter((row) => {
      if (statusFilter?.value && row.status !== statusFilter.value)
        return false;
      if (departmentFilter?.value && row.teamName !== departmentFilter.value)
        return false;
      return true;
    });
  }, [dateFilteredRows, statusFilter, departmentFilter]);

  const sortedRows = useMemo(() => {
    if (!sortedBy) return filteredRows;
    const getTime = (row) => {
      const date =
        sortedBy === 'taskDate'
          ? toDateValue(row.startTime)
          : toDateValue(row.createdAt || row.requestDate);
      return date ? date.getTime() : 0;
    };
    const sorted = [...filteredRows].sort((a, b) => getTime(a) - getTime(b));
    return reverseSort ? sorted.reverse() : sorted;
  }, [filteredRows, sortedBy, reverseSort]);

  useEffect(() => {
    setCurrentPage(1);
  }, [dateRange, statusFilter, departmentFilter, sortedBy, reverseSort]);

  const totalPages = Math.max(1, Math.ceil(sortedRows.length / PAGE_SIZE));
  const pagedRows = sortedRows.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const handleSortClick = (id, reverse) => {
    setSortedBy(id);
    setReverseSort(reverse);
  };

  const stats = useMemo(() => {
    const now = new Date();
    const isThisMonth = (row) => {
      const d = toDateValue(row.createdAt || row.requestDate);
      return (
        Boolean(d) &&
        d.getFullYear() === now.getFullYear() &&
        d.getMonth() === now.getMonth()
      );
    };
    const approved = displayRegList.filter(
      (r) => r.status === 'Approved' && isThisMonth(r)
    ).length;
    const declined = displayRegList.filter(
      (r) => r.status === 'Declined' && isThisMonth(r)
    ).length;
    return {
      total: displayRegList.length,
      pending: displayRegList.filter((r) => r.status === 'Pending').length,
      approved,
      declined,
      completed: approved + declined,
    };
  }, [displayRegList]);

  const exportToCSV = () => {
    if (sortedRows.length === 0) {
      addNotification({
        message: 'No data to export for the selected filters.',
        level: notificationStates.warning,
      });
      return;
    }
    const headers = [
      'ID',
      'Project',
      'Task Name',
      'Duration',
      'Task Date',
      'Department',
      'Report Date',
      'Assignee',
      'Status',
    ];
    const rows = sortedRows.map((row, index) => {
      const project = displayTeamProjects.find((p) => p.id === row.projectId);
      const taskDate = toDateValue(row.startTime);
      const reportDate = toDateValue(row.createdAt || row.requestDate);
      const assignee = displayMemberDetails?.[row.assignee];
      return [
        escapeCSVField(index + 1),
        escapeCSVField(project?.data?.project_name),
        escapeCSVField(row.taskName),
        escapeCSVField(`${row.taskHours || 0}h ${row.taskMinutes || 0}m`),
        escapeCSVField(taskDate ? taskDate.toLocaleDateString() : ''),
        escapeCSVField(row.teamName),
        escapeCSVField(reportDate ? reportDate.toLocaleDateString() : ''),
        escapeCSVField(assignee?.userName || assignee?.userEmailid),
        escapeCSVField(row.status),
      ].join(',');
    });
    const date = new Date().toISOString().split('T')[0];
    downloadCSV({
      headers,
      rows,
      filename: `regularization-requests-${date}.csv`,
    });
    addNotification({
      message: 'CSV exported successfully',
      level: notificationStates.success,
    });
  };

  const clearFilters = () => {
    setStatusFilter(REG_STATUS_FILTER_OPTIONS[0]);
    setDepartmentFilter(null);
  };

  const filterPopoverContent = (
    <div className={styles.filterPopover}>
      <Dropdown
        label="Status"
        options={REG_STATUS_FILTER_OPTIONS}
        value={statusFilter}
        onChange={setStatusFilter}
        selectStyles={borderedSelectStyles}
        isSearchable={false}
      />
      <Dropdown
        label="Department"
        options={departmentFilterOptions}
        value={departmentFilter}
        onChange={setDepartmentFilter}
        selectStyles={borderedSelectStyles}
        isSearchable={false}
      />
      <Button
        text="Clear filters"
        variant={buttonVariants.ghost}
        onClick={clearFilters}
      />
    </div>
  );

  const columns = [
    {
      id: 'id',
      name: 'ID',
      style: { width: '3%' },
      cellRenderer: (row, rowIndex) => (
        <Text
          variant={textVariantsEnum.Text_sm}
          fontWeight={fontWeightEnum.Medium}
        >
          {(currentPage - 1) * PAGE_SIZE + rowIndex + 1}
        </Text>
      ),
    },
    {
      id: 'project',
      name: 'Project / Task',
      style: { width: '20%' },
      cellRenderer: (row) => {
        const project = displayTeamProjects.find((p) => p.id === row.projectId);
        return <ProjectCell projectName={project?.data?.project_name} />;
      },
    },
    {
      id: 'duration',
      name: 'Duration',
      style: { width: '10%' },
      cellRenderer: (row) => (
        <PlainCell>
          {row.taskHours || 0}h {String(row.taskMinutes || 0).padStart(2, '0')}m
        </PlainCell>
      ),
    },
    {
      id: 'taskDate',
      name: 'Task Date',
      sortable: true,
      sortKey: 'taskDate',
      style: { width: '14%' },
      cellRenderer: (row) => <DateCell date={toDateValue(row.startTime)} />,
    },
    {
      id: 'reportDate',
      name: 'Report Date',
      sortable: true,
      sortKey: 'reportDate',
      style: { width: '14%' },
      cellRenderer: (row) => (
        <ReportDateCell date={toDateValue(row.createdAt || row.requestDate)} />
      ),
    },
    {
      id: 'assignee',
      name: 'Assignee',
      style: { width: '18%' },
      cellRenderer: (row) => (
        <AssigneeCell member={displayMemberDetails?.[row.assignee]} />
      ),
    },
    {
      id: 'status',
      name: 'Status',
      style: { width: '11%' },
      cellRenderer: (row) => (
        <StatusBadge
          label={row.status}
          tone={REG_STATUS_TONE[row.status] || statusBadgeTones.default}
          dot
        />
      ),
    },
    {
      id: 'actions',
      name: 'Actions',
      style: { width: '10%' },
      cellRenderer: (row) => (
        <Button
          icon={<FiMoreVertical size={14} />}
          variant={buttonVariants.outline}
          ariaLabel="Actions"
          onClick={(e) => onActionsButtonClick(e, row)}
        />
      ),
    },
  ];

  return (
    <>
      <div className={styles.regularizationPage}>
        <div className={styles.regularizationContainer}>
          <div className={styles.regularizationHeader}>
            <Button
              text="Add Request"
              icon={<FiPlus size={16} />}
              variant={buttonVariants.primary_rounded}
              onClick={() => setShowAddRequestModal(true)}
            />
            <div className={styles.regularizationHeaderActions}>
              <DateRangePicker value={dateRange} onChange={setDateRange} />
              <Popover
                content={filterPopoverContent}
                trigger="click"
                placement="bottomRight"
              >
                <Button
                  text="Filter"
                  icon={<FiFilter size={14} />}
                  variant={buttonVariants.outline}
                />
              </Popover>
              <Button
                text="Export"
                icon={<FiDownload size={14} />}
                variant={buttonVariants.outline}
                onClick={exportToCSV}
              />
            </div>
          </div>

          <div className={styles.statsRow}>
            {STAT_CARDS_CONFIG.map((card) => (
              <StatCard
                key={card.key}
                icon={card.icon}
                iconBg={card.iconBg}
                label={card.label}
                caption={card.caption}
                value={stats[card.key]}
              />
            ))}
          </div>

          <div className={styles.regularizationTableContainer}>
            {displayLoading ? (
              <div className="w-100 text-center py-5">
                <Spinner />
              </div>
            ) : sortedRows.length === 0 ? (
              <EmptyState
                illustration
                title="No Regularization Requests"
                description="No regularization requests match the selected filters."
              />
            ) : (
              <>
                <Table
                  className={styles.regularizationTable}
                  data={pagedRows}
                  columns={columns}
                  onSortClick={handleSortClick}
                  sortedBy={sortedBy}
                  reverseSort={reverseSort}
                />
                <div className={styles.tableFooter}>
                  <Text variant={textVariantsEnum.Text_sm} color="gray-500">
                    Showing {(currentPage - 1) * PAGE_SIZE + 1} to{' '}
                    {Math.min(currentPage * PAGE_SIZE, sortedRows.length)} of{' '}
                    {sortedRows.length} requests
                  </Text>
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <RegularizationModal
        isVisible={showAddRequestModal}
        teams={teams}
        fetchProjectTasks={fetchProjectTasks}
        onClose={() => {
          setShowAddRequestModal(false);
        }}
      />
      <UpdateRegularizationModal
        isVisible={showUpdateModal}
        rowData={selectedRequest}
        teamProjects={displayTeamProjects}
        teams={teams}
        fetchProjectTasks={fetchProjectTasks}
        fetchRegulationRequests={fetchRegulationRequests}
        onClose={() => {
          setShowUpdateModal(false);
        }}
      />
      <ViewRegularizationModal
        isVisible={Boolean(viewRequest)}
        rowData={viewRequest}
        teamProjects={displayTeamProjects}
        memberDetails={displayMemberDetails}
        onClose={() => setViewRequest(null)}
      />
      <ActionsMenu items={actionsMenuItems} ref={menuRef} />
      <ConfirmActionDialog
        visible={Boolean(deleteRequest)}
        onHide={() => setDeleteRequest(null)}
        title="Delete Regularization Request"
        message="Are you sure you want to delete this regularization request? This action cannot be undone."
        confirmLabel="Delete"
        onConfirm={handleDeleteRequest}
        loading={isDeleting}
      />
    </>
  );
};

RegularizationComponent.displayName = 'Regularization';
